import { useEffect, Component, ReactNode } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { LifeBuoy, Mail, MessageSquare } from 'lucide-react';
import { SITE_CONTACT_EMAIL, SITE_OPERATOR, SITE_OWNER } from '../lib/siteOperator';

export const HELP_SUPPORT_EMAIL = SITE_CONTACT_EMAIL;

export function HelpSupportFallback() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center mb-6">
          <LifeBuoy className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-800">Help & Support</h1>
        </div>
        <div id="email-support" className="p-4 bg-gray-50 rounded-lg border border-gray-200 scroll-mt-24">
          <div className="flex items-center mb-3">
            <Mail className="h-6 w-6 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-800">Email Support</h2>
          </div>
          <p className="text-gray-600 mb-4">
            {SITE_OPERATOR} operates My New Start. Owner: {SITE_OWNER}. For detailed inquiries, email us at{' '}
            <a href={`mailto:${HELP_SUPPORT_EMAIL}`} className="text-blue-600 hover:underline">
              {HELP_SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

class HelpPageBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <HelpSupportFallback />;
    }
    return this.props.children;
  }
}

const HelpContent = () => {
  const { language } = useLanguage();

  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center mb-6">
          <LifeBuoy className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-800">
            {language === 'en' ? 'Help & Support' : 'အကူအညီနှင့် ပံ့ပိုးမှု'}
          </h1>
        </div>

        <div className="space-y-6">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center mb-3">
              <MessageSquare className="h-6 w-6 text-blue-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-800">
                {language === 'en' ? 'Chat Support' : 'ချက်တင် အကူအညီ'}
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              {language === 'en'
                ? 'Use Mini Angel chat for instant help with your financial questions.'
                : 'သင့်ငွေကြေးဆိုင်ရာ မေးခွန်းများအတွက် Mini Angel ချက်တင်ကို အသုံးပြုပါ။'}
            </p>
          </div>

          <div id="email-support" className="p-4 bg-gray-50 rounded-lg border border-gray-200 scroll-mt-24">
            <div className="flex items-center mb-3">
              <Mail className="h-6 w-6 text-blue-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-800">
                {language === 'en' ? 'Email Support' : 'အီးမေးလ် အကူအညီ'}
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              {language === 'en'
                ? `${SITE_OPERATOR} operates My New Start. Owner: ${SITE_OWNER}. For detailed inquiries, email us at `
                : `${SITE_OPERATOR} က My New Start ကို ဆောင်ရွက်ပါတယ်။ ပိုင်ရှင် ${SITE_OWNER}။ အသေးစိတ်မေးမြန်းလိုပါက `}
              <a href={`mailto:${HELP_SUPPORT_EMAIL}`} className="text-blue-600 hover:underline">
                {HELP_SUPPORT_EMAIL}
              </a>
              {language === 'en' ? '' : ' သို့ အီးမေးလ်ပို့ပါ'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Help = () => (
  <HelpPageBoundary>
    <HelpContent />
  </HelpPageBoundary>
);

export default Help;